import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import BaseSubscriptionSelect, {
  type SubscriptionOption,
} from 'lumen/components/settings/users/base-subscription-select';

interface SubscriptionSelectSignature {
  Args: {
    excludeAllOption?: boolean;
    fullWidth?: boolean;
    isSecondary?: boolean;
    onChange: (selectedOption: SubscriptionOption | null) => void;
    value: SubscriptionOption | null;
  };
}

export default class SubscriptionSelect extends BaseSubscriptionSelect<SubscriptionSelectSignature> {
  readonly allSubscriptionsOption: SubscriptionOption;

  get allOptions(): SubscriptionOption[] {
    if (this.args.excludeAllOption) {
      return this.options;
    }

    return [this.allSubscriptionsOption, ...this.options];
  }

  get selectedOption(): SubscriptionOption | null {
    const value = this.args.value;
    if (!value) {
      return this.args.excludeAllOption ? null : this.allSubscriptionsOption;
    }

    return this.allOptions.find((option) => option.id === value.id) ?? value;
  }

  constructor(owner: Owner, args: SubscriptionSelectSignature['Args']) {
    super(owner, args);
    this.allSubscriptionsOption = {
      id: '',
      label: this.intl.t('settings.users.filter.all_subscriptions'),
    };
  }

  @action
  handleChange(option: SubscriptionOption | null): void {
    this.args.onChange(option?.id ? option : null);
  }

  <template>
    <PrismComboSelectInput
      @horizontalPosition="right"
      @isSearchable={{true}}
      @isSecondary={{@isSecondary}}
      @onChange={{this.handleChange}}
      @onOpen={{this.onOpen}}
      @onSearch={{this.onSearch}}
      @options={{this.allOptions}}
      @unsafe_matchTriggerWidth={{@fullWidth}}
      @unsafe_onLoadMore={{this.loadMoreTask.perform}}
      @value={{this.selectedOption}}
      data-test-id="subscription-select"
    />
  </template>
}

export type { SubscriptionOption };
